import { useQuery } from '@tanstack/react-query'

import { api } from 'services'
import {
  FEATURE_SWITCHES_OFF,
  type FeatureSwitches,
  isReferralsLive,
} from 'constants/featureSwitches'

/**
 * VRG-680 — the API's launch switches (`GET /features`). One query key for every reader (the
 * sidebar, Settings, the Home checklist, the Layout modal mount, the internal views), so they
 * can never disagree about whether a programme is live.
 *
 * `staleTime` 5 minutes: a switch is flipped by hand, rarely, and the API already sends a
 * 60-second Cache-Control. Not Infinity, so a flip reaches an open tab without a reload.
 */
export const FEATURES_QUERY_KEY = ['features'] as const

export const useFeatures = () =>
  useQuery<FeatureSwitches>({
    queryKey: FEATURES_QUERY_KEY,
    queryFn: async () => (await api.get('/features')).data,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

/**
 * Is "Refer & earn" live? FALSE while loading and on error (fail closed, see
 * `FEATURE_SWITCHES_OFF`). `isKnown` lets a route guard wait for the real answer instead of
 * redirecting a user away during the first fetch.
 */
export const useFeatureSwitches = (): { switches: FeatureSwitches; isKnown: boolean } => {
  const { data, isSuccess, isError } = useFeatures()
  // `data` is react-query's cached object, so identity is stable between renders and only
  // changes when the answer does — consumers key memoised corpora on it.
  return { switches: data ?? FEATURE_SWITCHES_OFF, isKnown: isSuccess || isError }
}

export const useReferralsEnabled = (): { enabled: boolean; isKnown: boolean } => {
  const { data, isSuccess, isError } = useFeatures()
  return {
    enabled: isReferralsLive(data ?? FEATURE_SWITCHES_OFF),
    isKnown: isSuccess || isError,
  }
}
